import React, { useState, useEffect, useLayoutEffect } from "react";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { ThemeProvider } from "@mui/material/styles";
import {
  Alert,
  AppBar,
  Toolbar,
  Button,
  Box,
  Container,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  InputBase,
  Select,
  MenuItem,
  Snackbar,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { selectors, actions } from "rdx";
import { QuoteModal } from "./QuoteModal";
import staticPageTheme from "./theme";

type NavItemT = {
  label: string;
  path: string;
};

type NavPathT = {
  key: string;
  path: string;
};

type StaticPageLayoutPropsT = {
  children: React.ReactNode;
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  noTopSpacer?: boolean;
};

const NAV_PATHS: NavPathT[] = [
  { key: "nav.home", path: "/" },
  { key: "nav.whySolar", path: "/solar" },
  { key: "nav.energyStorage", path: "/energy-storage" },
  { key: "nav.ambassador", path: "/ambassador" },
  { key: "nav.about", path: "/about" },
  { key: "nav.faq", path: "/faq" },
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Header = ({ userTag }: { userTag: string }) => {
  const { t } = useTranslation("static_layout");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [zip, setZip] = useState("");
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteZip, setQuoteZip] = useState("");
  const location = useLocation();

  const navItems: NavItemT[] = NAV_PATHS.map((item) => ({ label: t(item.key), path: item.path }));

  const currentPath = userTag ? location.pathname.replace(`/${userTag}`, "") || "/" : location.pathname;
  const isHome =
    currentPath === "/" ||
    currentPath === "/ambassador" ||
    currentPath === "/enterprise" ||
    currentPath === "/partners" ||
    currentPath === "/about" ||
    currentPath === "/join" ||
    currentPath === "/solar" ||
    currentPath === "/energy-storage" ||
    currentPath === "/careers" ||
    currentPath === "/faq";

  const handleGetQuote = () => {
    if (zip.trim().length !== 5) return;
    setQuoteZip(zip.trim());
    setQuoteOpen(true);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrolledBg = "linear-gradient(to bottom, rgba(10,15,26,0.92) 0%, rgba(10,15,26,0.75) 100%)";
  const prefix = userTag ? `/${userTag}` : "";

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          background: (() => {
            if (isHome && !scrolled) return "transparent";
            if (isHome) return scrolledBg;
            return "#0a0f1a";
          })(),
          borderBottom: isHome && !scrolled ? "none" : "1px solid rgba(255,255,255,0.08)",
          backdropFilter: isHome && scrolled ? "blur(8px)" : "none",
          transition: "background 0.35s ease, backdrop-filter 0.35s ease, border-color 0.35s ease",
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ justifyContent: "space-between" }}>
            <Link to={`${prefix}/`} style={{ display: "flex", alignItems: "center" }}>
              <img src="/img/assets/Logo - Light Background.png" alt="Powur" height={36} />
            </Link>

            <Box sx={{ display: { xs: "none", md: "flex" }, gap: 1, alignItems: "center", flexShrink: 0 }}>
              {navItems.map((item) => (
                <Button
                  key={item.path}
                  component={Link}
                  to={`${prefix}${item.path}`}
                  sx={{
                    color: currentPath === item.path ? "#f97316" : "white",
                    fontWeight: currentPath === item.path ? 700 : 500,
                    whiteSpace: "nowrap",
                    "&:hover": { color: "#f97316" },
                  }}
                >
                  {item.label}
                </Button>
              ))}
              <Box
                sx={{
                  ml: 2,
                  display: "flex",
                  alignItems: "stretch",
                  borderRadius: "50px",
                  overflow: "hidden",
                  border: "1px solid rgba(56,189,248,0.4)",
                  bgcolor: "rgba(255,255,255,0.05)",
                  height: "42px",
                }}
              >
                <InputBase
                  placeholder={t("header.zipPlaceholder")}
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleGetQuote()}
                  inputProps={{ maxLength: 10 }}
                  sx={{
                    color: "white",
                    pl: 2,
                    pr: 1,
                    fontSize: "0.85rem",
                    width: 130,
                    "& input::placeholder": { color: "rgba(255,255,255,0.45)", opacity: 1 },
                  }}
                />
                <Button
                  onClick={handleGetQuote}
                  variant="contained"
                  color="primary"
                  disableElevation
                  sx={{
                    borderRadius: 0,
                    px: 2.5,
                    whiteSpace: "nowrap",
                    fontSize: "0.85rem",
                    minWidth: "auto",
                    height: "100%",
                    boxShadow: "none",
                  }}
                >
                  {t("header.getAQuote")}
                </Button>
              </Box>
              <Button
                component={Link}
                to={`${prefix}/join`}
                variant="outlined"
                sx={{
                  ml: 1,
                  color: "white",
                  borderColor: "#38bdf8",
                  borderRadius: "50px",
                  height: "42px",
                  "&:hover": { borderColor: "#38bdf8", background: "rgba(56,189,248,0.08)", color: "white" },
                }}
              >
                {t("header.joinPowur")}
              </Button>
            </Box>

            <IconButton sx={{ display: { md: "none" }, color: "white" }} onClick={() => setDrawerOpen(true)}>
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: "#0a0f1a",
            color: "white",
          },
        }}
      >
        <Box sx={{ width: 260, pt: 2 }}>
          <List>
            {navItems.map((item) => (
              <ListItemButton
                key={item.path}
                component={Link}
                to={`${prefix}${item.path}`}
                selected={currentPath === item.path}
                onClick={() => setDrawerOpen(false)}
              >
                <ListItemText primary={item.label} />
              </ListItemButton>
            ))}
            <ListItemButton component={Link} to={`${prefix}/join`} onClick={() => setDrawerOpen(false)}>
              <ListItemText primary={t("drawer.joinPowur")} primaryTypographyProps={{ fontWeight: 700 }} />
            </ListItemButton>
            <Box sx={{ px: 2, pt: 1, pb: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  borderRadius: "50px",
                  overflow: "hidden",
                  border: "1px solid rgba(56,189,248,0.4)",
                  bgcolor: "rgba(255,255,255,0.05)",
                }}
              >
                <InputBase
                  placeholder={t("drawer.zipPlaceholder")}
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      setDrawerOpen(false);
                      handleGetQuote();
                    }
                  }}
                  inputProps={{ maxLength: 10 }}
                  sx={{
                    pl: 2,
                    pr: 1,
                    fontSize: "0.85rem",
                    flex: 1,
                    "& input::placeholder": { opacity: 1 },
                  }}
                />
                <Button
                  onClick={() => {
                    setDrawerOpen(false);
                    handleGetQuote();
                  }}
                  variant="contained"
                  color="primary"
                  sx={{ borderRadius: "0 50px 50px 0", px: 2, fontSize: "0.8rem", whiteSpace: "nowrap" }}
                >
                  {t("drawer.getAQuote")}
                </Button>
              </Box>
            </Box>
          </List>
        </Box>
      </Drawer>
      <QuoteModal open={quoteOpen} zip={quoteZip} onClose={() => setQuoteOpen(false)} />
    </>
  );
};

const Footer = ({ userTag }: { userTag: string }) => {
  const { t, i18n } = useTranslation("static_layout");
  const [footerZip, setFooterZip] = useState("");
  const [footerQuoteOpen, setFooterQuoteOpen] = useState(false);
  const [footerQuoteZip, setFooterQuoteZip] = useState("");
  const prefix = userTag ? `/${userTag}` : "";
  const currentLang = i18n.language?.split("-")[0] || "en";

  const handleFooterQuote = () => {
    if (footerZip.trim().length !== 5) return;
    setFooterQuoteZip(footerZip.trim());
    setFooterQuoteOpen(true);
  };

  return (
    <>
      <Box
        component="footer"
        sx={{
          bgcolor: "#0f172a",
          color: "white",
          py: 6,
          mt: "auto",
          overflowX: "hidden",
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "space-between",
              gap: 4,
            }}
          >
            <Box>
              <Box sx={{ mb: 2 }}>
                <img src="/img/assets/Logo - Light Background.png" alt="Powur" height={32} />
              </Box>
              <Typography variant="body2" sx={{ color: "grey.400", maxWidth: 300 }}>
                {t("footer.tagline")}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: { xs: 3, md: 6 }, flexWrap: "wrap" }}>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, color: "white" }}>
                  {t("footer.resources.heading")}
                </Typography>
                {[
                  { labelKey: "footer.resources.whySolar", path: "/solar" },
                  { labelKey: "footer.resources.ambassador", path: "/ambassador" },
                  { labelKey: "footer.resources.joinPowur", path: "/join" },
                  { labelKey: "footer.resources.enterprise", path: "/enterprise" },
                  { labelKey: "footer.resources.partners", path: "/partners" },
                ].map((item) => (
                  <Typography key={item.path} variant="body2" sx={{ mb: 0.5 }}>
                    <Link to={`${prefix}${item.path}`} style={{ color: "#94a3b8", textDecoration: "none" }}>
                      {t(item.labelKey)}
                    </Link>
                  </Typography>
                ))}
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <Link to="/login" style={{ color: "#94a3b8", textDecoration: "none" }}>
                    {t("footer.resources.powurLogin")}
                  </Link>
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, color: "white" }}>
                  {t("footer.company.heading")}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <Link to={`${prefix}/about`} style={{ color: "#94a3b8", textDecoration: "none" }}>
                    {t("footer.company.about")}
                  </Link>
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <Link to={`${prefix}/licenses`} style={{ color: "#94a3b8", textDecoration: "none" }}>
                    {t("footer.company.stateLicenses")}
                  </Link>
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <a
                    href={process.env.REACT_APP_INCOME_DISCLOSURE_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#94a3b8", textDecoration: "none" }}
                  >
                    {t("footer.company.incomeDisclosure")}
                  </a>
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <Link to={`${prefix}/careers`} style={{ color: "#94a3b8", textDecoration: "none" }}>
                    {t("footer.company.careers")}
                  </Link>
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <a
                    href="https://outlet.powur.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#94a3b8", textDecoration: "none" }}
                  >
                    {t("footer.company.store")}
                  </a>
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <a
                    href="https://blog.powur.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#94a3b8", textDecoration: "none" }}
                  >
                    {t("footer.company.powurBlog")}
                  </a>
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, color: "white" }}>
                  {t("footer.contact.heading")}
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <a
                    href="https://chat.powur.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "none" }}
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 1,
                        px: 2,
                        py: 0.75,
                        borderRadius: "9999px",
                        background: "linear-gradient(90deg, #3b82f6, #38bdf8)",
                        color: "white",
                        fontSize: "0.8rem",
                        fontFamily: "'Poppins', sans-serif",
                        fontWeight: 600,
                        cursor: "pointer",
                        transition: "opacity 0.2s ease",
                        "&:hover": { opacity: 0.85 },
                      }}
                    >
                      {t("footer.contact.liveChat")}
                    </Box>
                  </a>
                </Box>
                <Typography variant="body2" sx={{ color: "#94a3b8", mb: 0.5 }}>
                  {t("footer.contact.needHelp")}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <a href="mailto:support@powur.com" style={{ color: "#94a3b8", textDecoration: "none" }}>
                    support@powur.com
                  </a>
                </Typography>
              </Box>
              <Box sx={{ minWidth: { xs: 0, sm: 220 }, width: { xs: "100%", sm: "auto" } }}>
                <Typography variant="subtitle2" sx={{ mb: 2, color: "white" }}>
                  {t("footer.quote.heading")}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "stretch",
                    borderRadius: "50px",
                    overflow: "hidden",
                    border: "1px solid rgba(56,189,248,0.4)",
                    bgcolor: "rgba(255,255,255,0.05)",
                    height: "42px",
                    mb: 2,
                  }}
                >
                  <InputBase
                    placeholder={t("footer.quote.zipPlaceholder")}
                    value={footerZip}
                    onChange={(e) => setFooterZip(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleFooterQuote()}
                    inputProps={{ maxLength: 10 }}
                    sx={{
                      pl: 2,
                      flex: 1,
                      color: "white",
                      fontSize: "0.85rem",
                      "& ::placeholder": { color: "rgba(255,255,255,0.7)" },
                    }}
                  />
                  <Button
                    onClick={handleFooterQuote}
                    sx={{
                      borderRadius: "50px",
                      px: 2,
                      background: "linear-gradient(90deg, #f97316, #fb923c)",
                      color: "white",
                      fontFamily: "'Poppins'",
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      whiteSpace: "nowrap",
                      "&:hover": { background: "linear-gradient(90deg, #ea6c0a, #f97316)" },
                    }}
                  >
                    {t("footer.quote.checkSavings")}
                  </Button>
                </Box>
                <Select
                  value={currentLang}
                  onChange={(e) => i18n.changeLanguage(e.target.value)}
                  size="small"
                  fullWidth
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    fontSize: "0.85rem",
                    borderRadius: "9999px",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderRadius: "9999px",
                      borderColor: "rgba(56,189,248,0.3)",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(56,189,248,0.6)" },
                    "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.5)" },
                    bgcolor: "rgba(255,255,255,0.04)",
                  }}
                >
                  <MenuItem value="en" sx={{ color: "#374151", fontSize: "0.85rem" }}>
                    {t("footer.language.english")}
                  </MenuItem>
                  <MenuItem value="es" sx={{ color: "#374151", fontSize: "0.85rem" }}>
                    {t("footer.language.spanish")}
                  </MenuItem>
                </Select>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, color: "white" }}>
                  {t("footer.legal.heading")}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <Link to={`${prefix}/privacy-policy`} style={{ color: "#94a3b8", textDecoration: "none" }}>
                    {t("footer.legal.privacyPolicy")}
                  </Link>
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <a
                    href="https://powur-public2.s3.us-west-1.amazonaws.com/application_agreements/Powur_Seller_Policies_and_Procedures_2025-10-31.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#94a3b8", textDecoration: "none" }}
                  >
                    {t("footer.legal.termsAndConditions")}
                  </a>
                </Typography>
              </Box>
            </Box>
          </Box>
          <Typography variant="body2" sx={{ color: "grey.600", mt: 4, pt: 4, borderTop: "1px solid #334155" }}>
            {t("footer.copyright", { year: new Date().getFullYear() })}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "grey.700", mt: 3, fontSize: "0.72rem", lineHeight: 1.7, maxWidth: 900 }}
          >
            {t("footer.disclaimer")}
          </Typography>
        </Container>
      </Box>
      <QuoteModal open={footerQuoteOpen} zip={footerQuoteZip} onClose={() => setFooterQuoteOpen(false)} />
    </>
  );
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const StaticPageLayout = ({
  children,
  title,
  description,
  keywords,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogImage = "/img/powur_fb_01.jpg",
  twitterTitle,
  twitterDescription,
  noTopSpacer,
}: StaticPageLayoutPropsT) => {
  const { i18n } = useTranslation("static_layout");
  const { user } = useParams<{ user?: string }>();
  const userTag = user || "";
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const alertMessage = useSelector(selectors.selectAlertMessage) as any;
  const closeAlertMessage = () => {
    dispatch(actions.clearAlertMessage());
  };

  const metaLanguage = i18n.language?.split("-")[0] === "es" ? "Spanish" : "English";

  useLayoutEffect(() => {
    document.body.style.backgroundColor = "#0a0f1a";
    return () => {
      document.body.style.backgroundColor = "";
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") history.push("/login");
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [history]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const refId = params.get("ref_id");
    if (refId || user) {
      const isHomepage = location.pathname.split("/").filter(Boolean).length === (user ? 1 : 0);
      dispatch(
        actions.getPageReferrer({
          ref_id: refId,
          slug: user,
          pageName: isHomepage ? "home" : undefined,
          history,
        }),
      );
    }
  }, [user, location.search, location.pathname, dispatch, history]);

  return (
    <ThemeProvider theme={staticPageTheme}>
      <Snackbar
        open={!!alertMessage.visible && !!alertMessage.message}
        autoHideDuration={alertMessage.duration}
        onClose={closeAlertMessage}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={closeAlertMessage} severity={alertMessage.severity}>
          {alertMessage.message}
        </Alert>
      </Snackbar>
      <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
        {title && (
          <Helmet>
            <title>{title}</title>
            <meta name="title" content={title} />
            {description && <meta name="description" content={description} />}
            {keywords && <meta name="keywords" content={keywords} />}
            <meta name="robots" content="index, follow" />
            <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
            <meta name="language" content={metaLanguage} />

            <meta property="og:type" content="website" />
            {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
            <meta property="og:title" content={ogTitle || title} />
            {(ogDescription || description) && (
              <meta property="og:description" content={ogDescription || description} />
            )}
            <meta property="og:image" content={ogImage} />

            <meta property="twitter:card" content="summary_large_image" />
            {canonicalUrl && <meta property="twitter:url" content={canonicalUrl} />}
            <meta property="twitter:title" content={twitterTitle || ogTitle || title} />
            {(twitterDescription || ogDescription || description) && (
              <meta property="twitter:description" content={twitterDescription || ogDescription || description} />
            )}
            <meta property="twitter:image" content={ogImage} />
          </Helmet>
        )}
        <ScrollToTop />
        <Header userTag={userTag} />
        {!noTopSpacer && <Box sx={{ height: "64px" }} />}
        <Box component="main" sx={{ flex: 1 }}>
          {children}
        </Box>
        <Footer userTag={userTag} />
      </Box>
    </ThemeProvider>
  );
};

export default StaticPageLayout;
